import React from 'react';
import styled from 'styled-components';
import { StatBar } from './primitives';
import { LabelWithHelp } from './FieldHelp';
import { readMeter } from '../services/instructorMeterLimits';

const Strip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
`;

const Pill = styled.div`
  min-width: 168px;
  flex: 1 1 168px;
  max-width: 240px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface2};
`;

const PillLabel = styled.div`
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 650;
  color: ${({ theme }) => theme.textMuted};
  margin-bottom: 4px;
`;

const PillValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  font-variant-numeric: tabular-nums;
`;

const PillMeta = styled.div`
  font-size: ${({ theme }) => theme.fsMicro};
  color: ${({ theme }) => theme.textSubtle};
  margin-top: 6px;
`;

/**
 * One plan meter pill (used / limit) for list-screen heroes.
 */
export function InstructorMeterPill({
  entitlements,
  meterKey,
  label,
  help,
}) {
  const { limit, used } = readMeter(entitlements, meterKey);
  const unlimited = limit == null;
  const pct = unlimited || limit === 0
    ? 0
    : Math.min((used / limit) * 100, 100);
  const atCap = !unlimited && limit > 0 && used >= limit;

  return (
    <Pill>
      <PillLabel>
        <LabelWithHelp help={help}>{label}</LabelWithHelp>
      </PillLabel>
      <PillValue className="tnum">
        {used}
        {!unlimited && <small style={{ fontWeight: 600, opacity: 0.72 }}> / {limit}</small>}
        {unlimited && <small style={{ fontWeight: 600, opacity: 0.72 }}> / ∞</small>}
      </PillValue>
      <StatBar style={{ marginTop: 8, height: 5 }}>
        <i style={{
          width: `${pct}%`,
          background: atCap
            ? 'linear-gradient(90deg, #e85d6c, #d6455d)'
            : undefined,
        }}
        />
      </StatBar>
      <PillMeta>
        {unlimited ? 'Unlimited on your plan' : (atCap ? 'Plan limit reached' : 'of plan limit')}
      </PillMeta>
    </Pill>
  );
}

/**
 * Row of meter pills under a list-screen hero subtitle.
 */
export function InstructorMeterStrip({ entitlements, meters }) {
  if (!entitlements?.meters) {
    return null;
  }
  return (
    <Strip>
      {meters.map(({ key, label, help }) => (
        <InstructorMeterPill
          key={key}
          entitlements={entitlements}
          meterKey={key}
          label={label}
          help={help}
        />
      ))}
    </Strip>
  );
}
